import React, { useEffect, useRef } from 'react';

const CinematicHeroBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    let width = 0;
    let height = 0;
    let dpr = 1;

    // Handle full-screen viewport resize
    const handleResize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.parentElement ? canvas.parentElement.clientWidth : window.innerWidth;
      height = canvas.parentElement ? canvas.parentElement.clientHeight : window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
    };

    handleResize();
    const resizeObserver = new ResizeObserver(() => handleResize());
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }
    window.addEventListener('resize', handleResize);

    // Theme Palette
    const COLOR_EMERALD = '#00ffd1';
    const COLOR_AQUA = '#00e5ff';
    const COLOR_GREEN = '#10b981';
    const COLOR_WHITE = '#ffffff';

    // Particle system distributed across the full screen
    const PARTICLE_COUNT = 200;
    const particles = [];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * (width || 1200),
        y: Math.random() * (height || 800),
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        baseRadius: Math.random() * 2 + 1,
        alpha: Math.random() * 0.6 + 0.4,
        pulseSpeed: 0.02 + Math.random() * 0.03,
        pulseAngle: Math.random() * Math.PI * 2,
        targetX: width / 2,
        targetY: height / 2
      });
    }

    // Mathematical Formulas & Figures
    const mathFormulas = [
      '∫ f(x)dx', 'd²P/dt²', 'Σ (x-μ)²', 'α = 3.84', 'β = 0.92',
      'σ² = 0.04', '∇ × F', 'λ = 0.08ms', 'ΔP = +0.42%', 'E(R)',
      'Sharpe = 3.84', 'Cov(X,Y)', 'P(A|B)', '∂V/∂t', 'dS/S',
      'f(t) = e^(-λt)', 'dK/dt', 'Var(X)', 'N(d1)', 'N(d2)',
      'lim Δt→0', '∫₀^∞ e^-rT', 'max E[U(W)]', 'δP/δS'
    ];

    // Numbers & Market Ticks
    const numbersList = [
      '18,421.50', '0.0042', '+3.84σ', '101101', '0.08ms',
      '+12.4%', '1,250 QTY', '42.80', 'Δ+0.85', '18,422.75',
      '0.00014s', '+0.42%', 'VOL 84.2M', 'BUY 1,500', '18,425.00',
      'INR 18,420', '+0.28%', '99.98% ACC', 'ALPHA +4.2'
    ];

    let blastItems = [];
    let shockwaves = [];
    let time = 0;
    let lastCycle = -1;

    // Trigger Mega Full-Screen Explosion
    const triggerMegaBlast = (cx, cy) => {
      blastItems = [];
      shockwaves = [];

      // 1. Massive Shockwaves expanding outward across the entire screen
      shockwaves.push({ x: cx, y: cy, r: 10, maxR: Math.max(width, height) * 1.5, alpha: 1, color: COLOR_EMERALD, width: 3 });
      shockwaves.push({ x: cx, y: cy, r: 5, maxR: Math.max(width, height) * 1.2, alpha: 0.9, color: COLOR_AQUA, width: 2 });
      shockwaves.push({ x: cx, y: cy, r: 2, maxR: Math.max(width, height) * 0.9, alpha: 0.8, color: COLOR_WHITE, width: 1.5 });

      // Distance required to reach the farthest corners of the screen
      const maxCornerDist = Math.hypot(cx, cy);

      // 2. HUGE Mathematical Figures & Formulas Blasting Outward across full screen to both corners
      for (let i = 0; i < 36; i++) {
        const angle = (i / 36) * Math.PI * 2 + (Math.random() - 0.5) * 0.2;
        const speed = 6.0 + Math.random() * 8.0; // Fast initial blast speed
        const text = mathFormulas[i % mathFormulas.length];
        
        // Target destination spanning full screen width and height into all 4 corners
        const distRatio = 0.22 + Math.random() * 0.74; // Spans from center up to 96% of corner distance
        let destX = cx + Math.cos(angle) * (maxCornerDist * distRatio);
        let destY = cy + Math.sin(angle) * (maxCornerDist * distRatio);

        // Keep within safe viewport boundaries (3% to 97%)
        destX = Math.max(width * 0.03, Math.min(width * 0.97, destX));
        destY = Math.max(height * 0.06, Math.min(height * 0.94, destY));

        blastItems.push({
          type: 'math',
          text,
          x: cx,
          y: cy,
          destX,
          destY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          alpha: 1,
          size: Math.floor(16 + Math.random() * 12), // Huge clear size
          color: Math.random() > 0.3 ? COLOR_EMERALD : COLOR_AQUA,
          life: 0,
          morphed: false,
          candleHeight: 24 + Math.random() * 36,
          wickTop: 10 + Math.random() * 16,
          wickBottom: 10 + Math.random() * 16,
          isGreen: Math.random() > 0.35
        });
      }

      // 3. Quant Market Numbers Blasting Outward across full screen to both corners
      for (let i = 0; i < 32; i++) {
        const angle = (i / 32) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const speed = 5.0 + Math.random() * 7.0;
        const text = numbersList[i % numbersList.length];
        
        const distRatio = 0.20 + Math.random() * 0.76;
        let destX = cx + Math.cos(angle) * (maxCornerDist * distRatio);
        let destY = cy + Math.sin(angle) * (maxCornerDist * distRatio);

        destX = Math.max(width * 0.03, Math.min(width * 0.97, destX));
        destY = Math.max(height * 0.06, Math.min(height * 0.94, destY));

        blastItems.push({
          type: 'number',
          text,
          x: cx,
          y: cy,
          destX,
          destY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          alpha: 1,
          size: Math.floor(14 + Math.random() * 10),
          color: COLOR_WHITE,
          life: 0,
          morphed: false,
          candleHeight: 22 + Math.random() * 32,
          wickTop: 8 + Math.random() * 14,
          wickBottom: 8 + Math.random() * 14,
          isGreen: Math.random() > 0.4
        });
      }
    };

    const render = () => {
      time += 0.015;
      ctx.clearRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;

      // Deep dark charcoal background
      ctx.fillStyle = '#03060a';
      ctx.fillRect(0, 0, width, height);

      // Ambient radial center glow
      const bgGrad = ctx.createRadialGradient(
        cx, cy, 20,
        cx, cy, Math.max(width, height) * 0.75
      );
      bgGrad.addColorStop(0, 'rgba(0, 255, 209, 0.12)');
      bgGrad.addColorStop(0.4, 'rgba(0, 229, 255, 0.04)');
      bgGrad.addColorStop(1, 'rgba(3, 6, 10, 0)');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      // Loop Cycle (11 seconds total)
      const CYCLE_DURATION = 11;
      const currentCycleCount = Math.floor(time / CYCLE_DURATION);
      const cycleProgress = (time % CYCLE_DURATION) / CYCLE_DURATION;

      // Phase timing thresholds
      // 0.00 - 0.28: Singularity Convergence at Center
      // 0.28 - 0.32: MEGA BLAST EXPLOSION
      // 0.32 - 0.75: Math & Numbers expand & Morph into Stock Candlesticks
      // 0.75 - 1.00: Dissolution & Gravity pull back to center

      if (currentCycleCount !== lastCycle && cycleProgress >= 0.28) {
        lastCycle = currentCycleCount;
        triggerMegaBlast(cx, cy);
      }

      // --- PHASE 0: CENTER SINGULARITY (0.00 - 0.28) ---
      if (cycleProgress < 0.28) {
        const coreRatio = cycleProgress / 0.28; // 0 to 1
        const coreRadius = (1 - coreRatio) * 120 + 8; // shrinking into tiny core
        
        ctx.save();
        // Swirling center core glow
        const coreGlow = ctx.createRadialGradient(cx, cy, 2, cx, cy, 60 * (1 - coreRatio * 0.5));
        coreGlow.addColorStop(0, '#ffffff');
        coreGlow.addColorStop(0.4, COLOR_EMERALD);
        coreGlow.addColorStop(1, 'rgba(0, 255, 209, 0)');
        ctx.fillStyle = coreGlow;
        ctx.beginPath();
        ctx.arc(cx, cy, 80 * (1 - coreRatio * 0.5), 0, Math.PI * 2);
        ctx.fill();

        // Swirling math equations around singularity core
        ctx.font = '600 13px "JetBrains Mono", monospace';
        ctx.fillStyle = COLOR_EMERALD;
        mathFormulas.slice(0, 8).forEach((formula, idx) => {
          const orbitAngle = (idx / 8) * Math.PI * 2 + time * 1.5;
          const r = coreRadius + Math.sin(time * 2 + idx) * 15;
          const fx = cx + Math.cos(orbitAngle) * r;
          const fy = cy + Math.sin(orbitAngle) * r;
          ctx.fillText(formula, fx, fy);
        });
        ctx.restore();
      }

      // --- DRAW SHOCKWAVES ---
      shockwaves.forEach((sw) => {
        sw.r += 12; // High speed full-screen expansion
        sw.alpha *= 0.96;
        if (sw.alpha > 0.02 && sw.r < sw.maxR) {
          ctx.beginPath();
          ctx.arc(sw.x, sw.y, sw.r, 0, Math.PI * 2);
          ctx.strokeStyle = sw.color;
          ctx.lineWidth = sw.width;
          ctx.globalAlpha = Math.max(0, sw.alpha);
          ctx.shadowColor = sw.color;
          ctx.shadowBlur = 16;
          ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.globalAlpha = 1;
        }
      });

      // --- UPDATE & DRAW PARTICLES ---
      particles.forEach((p, i) => {
        if (cycleProgress < 0.28) {
          // Pull into center
          p.targetX = cx + (Math.random() - 0.5) * 20;
          p.targetY = cy + (Math.random() - 0.5) * 20;
          p.x += (p.targetX - p.x) * 0.08;
          p.y += (p.targetY - p.y) * 0.08;
        } else if (cycleProgress < 0.75) {
          // Explode out across full screen to both corners
          const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + (i % 7) * 0.1;
          const maxCornerRadius = Math.hypot(cx, cy) * 0.95;
          const radius = maxCornerRadius * ((cycleProgress - 0.28) / 0.47);
          p.targetX = cx + Math.cos(angle) * radius;
          p.targetY = cy + Math.sin(angle) * radius;
          p.x += (p.targetX - p.x) * 0.06;
          p.y += (p.targetY - p.y) * 0.06;
        } else {
          // Gravity pull back to center for next cycle
          p.targetX = cx;
          p.targetY = cy;
          p.x += (p.targetX - p.x) * 0.05;
          p.y += (p.targetY - p.y) * 0.05;
        }

        p.pulseAngle += p.pulseSpeed;
        const currentAlpha = p.alpha * (0.5 + 0.5 * Math.sin(p.pulseAngle));

        // Connect lines between nearby particles across full screen
        for (let j = i + 1; j < PARTICLE_COUNT; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const distSq = dx * dx + dy * dy;
          const maxDist = cycleProgress >= 0.28 && cycleProgress < 0.75 ? 95 : 60;

          if (distSq < maxDist * maxDist) {
            const dist = Math.sqrt(distSq);
            const lineAlpha = (1 - dist / maxDist) * 0.35 * currentAlpha;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(0, 255, 209, ${lineAlpha})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }

        // Render particle dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.baseRadius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 255, 209, ${currentAlpha})`;
        ctx.shadowColor = COLOR_EMERALD;
        ctx.shadowBlur = 6;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // --- UPDATE & DRAW BLAST ITEMS (MATH -> CANDLESTICKS) ---
      let morphedCandles = [];

      blastItems.forEach((item) => {
        // Move towards destination
        item.x += (item.destX - item.x) * 0.08 + item.vx * 0.15;
        item.y += (item.destY - item.y) * 0.08 + item.vy * 0.15;
        item.vx *= 0.92;
        item.vy *= 0.92;
        item.life += 0.015;

        // Alpha fade sequence
        if (cycleProgress < 0.28) {
          item.alpha = 0;
        } else if (cycleProgress > 0.85) {
          item.alpha = Math.max(0, (1 - cycleProgress) / 0.15);
        } else {
          item.alpha = 1;
        }

        // Transition: Math numbers morph into Stock Candlesticks around cycleProgress = 0.48
        const morphRatio = Math.min(1, Math.max(0, (cycleProgress - 0.40) / 0.15));

        ctx.globalAlpha = item.alpha;

        if (morphRatio < 0.8) {
          // Render HUGE Math Formulas and Numbers exploding outward
          const currentSize = Math.max(12, item.size * (1 - morphRatio * 0.3));
          ctx.font = `700 ${currentSize}px "JetBrains Mono", monospace, sans-serif`;
          ctx.fillStyle = item.color;
          ctx.shadowColor = item.color;
          ctx.shadowBlur = 12 * (1 - morphRatio);
          ctx.fillText(item.text, item.x, item.y);
          ctx.shadowBlur = 0;
        }

        if (morphRatio > 0.2) {
          // MORPH INTO STOCK MARKET CANDLESTICKS!
          const candleAlpha = item.alpha * morphRatio;
          const isGreen = item.isGreen;
          const candleColor = isGreen ? COLOR_EMERALD : COLOR_AQUA;

          ctx.globalAlpha = candleAlpha;
          ctx.shadowColor = candleColor;
          ctx.shadowBlur = 10;

          // Upper and Lower Wicks
          ctx.beginPath();
          ctx.moveTo(item.x, item.y - item.wickTop);
          ctx.lineTo(item.x, item.y + item.candleHeight + item.wickBottom);
          ctx.strokeStyle = candleColor;
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // Candlestick Body
          ctx.fillStyle = candleColor;
          ctx.fillRect(item.x - 4, item.y, 8, item.candleHeight);

          // Top Price Label above Candlestick
          ctx.font = '600 9px "JetBrains Mono", monospace';
          ctx.fillStyle = COLOR_WHITE;
          ctx.fillText(isGreen ? '+0.42%' : '-0.15%', item.x - 12, item.y - item.wickTop - 4);

          ctx.shadowBlur = 0;

          morphedCandles.push({ x: item.x, y: item.y - item.wickTop });
        }

        ctx.globalAlpha = 1;
      });



      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        backgroundColor: '#03060a'
      }}
      data-testid="cinematic-hero-background"
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />
    </div>
  );
};

export default React.memo(CinematicHeroBackground);

